import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@shared/components/ui/chart";
import { formatNumber, formatPercent } from "@shared/lib/format";
import type { FunnelStep } from "@shared/lib/admin-metrics";

/**
 * Funnel d'acquisition : comparaison de grandeurs sur des libellés longs, donc
 * des barres horizontales et une seule teinte — la couleur ne porte ici aucune
 * identité, seulement la magnitude. Valeurs étiquetées directement au bout de
 * chaque barre, infobulle au survol pour le détail (pages vues, conversion).
 */
const chartConfig = {
  uniques: { label: "Personnes", color: "var(--ordinal-4)" },
} satisfies ChartConfig;

export function FunnelChart({ steps }: { steps: FunnelStep[] }) {
  const data = steps.map((step) => ({
    label: step.label,
    uniques: step.uniques,
    total: step.total,
    conversion: step.conversionFromPrevious,
  }));

  return (
    <ChartContainer className="aspect-auto h-[280px] w-full" config={chartConfig}>
      <BarChart
        accessibilityLayer
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 48, bottom: 4, left: 4 }}
      >
        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
        <XAxis type="number" hide />
        <YAxis
          axisLine={false}
          dataKey="label"
          tickLine={false}
          tickMargin={8}
          type="category"
          width={190}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value, _name, item) => {
                const row = item.payload as { total: number; conversion: number | null };
                return (
                  <div className="grid gap-0.5">
                    <span className="font-semibold tabular-nums">
                      {formatNumber(Number(value))} personne{Number(value) > 1 ? "s" : ""}
                    </span>
                    <span className="text-muted-foreground">{formatNumber(row.total)} évènements</span>
                    {row.conversion !== null && (
                      <span className="text-muted-foreground">
                        {formatPercent(row.conversion)} de l’étape précédente
                      </span>
                    )}
                  </div>
                );
              }}
              hideIndicator
            />
          }
          cursor={false}
        />
        <Bar dataKey="uniques" fill="var(--color-uniques)" radius={[0, 4, 4, 0]}>
          <LabelList
            className="fill-foreground"
            dataKey="uniques"
            fontSize={11}
            offset={8}
            position="right"
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}
